import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { AdminLayout, btn, btnGhost, inp, lbl } from "@/pages/admin/adminShared";
import { formatDate, formatDateTime, todayISO } from "@/lib/format";
import { Plus, Search, ChevronUp, ChevronDown, X, Copy, Send, Save, CheckCircle2, ListMusic, GitCompare, Database, FileSearch } from "lucide-react";

const TAGS = ["New", "Recap", "Request", "Warm-up", "Cool-down"];
const ACTS = ["taught", "reviewed", "danced"];
const STATE_STYLE = { draft: "bg-secondary text-[#5C4A42]", published: "bg-[#FEF3C7] text-[#92600A]", completed: "bg-[#C85A32] text-white" };

function Builder({ session, dances, onChange }) {
  const [items, setItems] = useState(session.items.map((i) => ({ dance: i.dance, note: i.note, plays: i.plays || 1, tag: i.tag || "" })));
  const [notes, setNotes] = useState(session.notes || "");
  const [q, setQ] = useState("");
  const completed = session.state === "completed";

  const save = async (publish) => {
    try {
      const body = { items: items.map((it, idx) => ({ dance_id: it.dance.id, position: idx, note: it.note, plays: Number(it.plays) || 1, tag: it.tag })), notes };
      await api.put(`/admin/us/sessions/${session.id}/items`, body);
      if (publish !== undefined) await api.post(`/admin/us/sessions/${session.id}/publish?publish=${publish}`);
      toast.success(publish === true ? "Published to members" : publish === false ? "Unpublished" : "Draft saved");
      onChange();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const copyPrev = async () => {
    try { const { data } = await api.post(`/admin/us/sessions/${session.id}/copy-previous`); setItems(data.items.map((i) => ({ dance: i.dance, note: i.note, plays: i.plays || 1, tag: i.tag || "" }))); toast.success("Copied previous playlist"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const results = q ? (dances || []).filter((d) => d.name.toLowerCase().includes(q.toLowerCase()) && !items.find((it) => it.dance.id === d.id)).slice(0, 6) : [];

  return (
    <div data-testid="us-builder">
      {!completed && (
        <div className="mb-3">
          <div className="flex items-center gap-2 rounded-xl border border-border px-3"><Search size={16} className="text-[#6E5B51]" /><input className="w-full bg-transparent py-2.5 outline-none" placeholder="Search dances to add" value={q} onChange={(e) => setQ(e.target.value)} data-testid="us-dance-search" /></div>
          {results.map((d) => <button key={d.id} onClick={() => { setItems((p) => [...p, { dance: d, note: "", plays: 1, tag: "" }]); setQ(""); }} data-testid={`us-add-${d.slug}`} className="mt-1 flex w-full items-center justify-between rounded-lg border border-border px-3 py-2 text-left text-sm hover:border-[#C85A32]"><span className="font-semibold text-[#2D1E18]">{d.name}</span><Plus size={14} /></button>)}
        </div>
      )}
      {items.length === 0 ? <EmptyState title="No dances yet" subtitle="Search above to add dances." /> : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={it.dance.id} className="rounded-xl border border-border bg-card p-3" data-testid={`us-item-${it.dance.slug}`}>
              <div className="flex items-center gap-2">
                <span className="flex-1 font-semibold text-[#2D1E18]">{it.dance.name}</span>
                <LevelBadge level={it.dance.level} />
                {!completed && <>
                  <button onClick={() => setItems((p) => { const a = [...p]; if (i <= 0) return p; [a[i - 1], a[i]] = [a[i], a[i - 1]]; return a; })} aria-label="Move up" className="rounded-lg p-2.5 text-[#6E5B51] hover:bg-secondary"><ChevronUp size={16} /></button>
                  <button onClick={() => setItems((p) => { const a = [...p]; if (i >= a.length - 1) return p; [a[i + 1], a[i]] = [a[i], a[i + 1]]; return a; })} aria-label="Move down" className="rounded-lg p-2.5 text-[#6E5B51] hover:bg-secondary"><ChevronDown size={16} /></button>
                  <button onClick={() => setItems((p) => p.filter((x) => x.dance.id !== it.dance.id))} aria-label="Remove" className="rounded-lg p-2.5 text-[#6E5B51] hover:text-[#991B1B]"><X size={16} /></button>
                </>}
              </div>
              {!completed && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <select className={`${inp} w-32`} value={it.tag} onChange={(e) => setItems((p) => p.map((x, idx) => idx === i ? { ...x, tag: e.target.value } : x))} data-testid={`us-tag-${it.dance.slug}`}><option value="">No tag</option>{TAGS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
                  <input type="number" min="1" className={`${inp} w-20`} value={it.plays} onChange={(e) => setItems((p) => p.map((x, idx) => idx === i ? { ...x, plays: e.target.value } : x))} aria-label="Plays" />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!completed && (
        <>
          <textarea className={`${inp} mt-3`} rows={2} placeholder="Session notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="mt-3 flex flex-wrap gap-2 sticky bottom-16 z-10 -mx-2 rounded-xl bg-card/95 px-2 py-2 backdrop-blur md:static md:mx-0 md:bg-transparent md:p-0">
            <button className={btn} onClick={() => save(undefined)} data-testid="us-save-draft"><Save size={15} /> Save draft</button>
            {session.state === "draft"
              ? <button className={btnGhost} onClick={() => save(true)} data-testid="us-publish"><Send size={15} /> Publish to members</button>
              : <button className={btnGhost} onClick={() => save(false)} data-testid="us-unpublish"><X size={15} /> Unpublish</button>}
            <button className={btnGhost} onClick={copyPrev} data-testid="us-copy-prev"><Copy size={15} /> Copy previous</button>
          </div>
        </>
      )}
    </div>
  );
}

function CompletePanel({ session, onChange }) {
  const [outcome, setOutcome] = useState(() => {
    const m = {};
    (session.outcome || []).forEach((o) => { m[o.dance_id] = new Set(o.activities); });
    session.items.forEach((i) => { if (!m[i.dance.id]) m[i.dance.id] = new Set(["danced"]); });
    return m;
  });
  const toggle = (did, act) => setOutcome((p) => { const n = { ...p }; const s = new Set(n[did]); s.has(act) ? s.delete(act) : s.add(act); n[did] = s; return n; });
  const submit = async () => {
    try {
      const body = { outcome: session.items.map((i) => ({ dance_id: i.dance.id, activities: [...(outcome[i.dance.id] || [])] })) };
      const url = session.state === "completed" ? `/admin/us/sessions/${session.id}/outcome` : `/admin/us/sessions/${session.id}/complete`;
      const method = session.state === "completed" ? api.put : api.post;
      await method(url, body);
      toast.success(session.state === "completed" ? "Completed session updated (audited)" : "Session completed");
      onChange();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div data-testid="us-complete">
      <p className="mb-2 text-sm text-[#6E5B51]">Record what actually happened. Publishing earlier did not create any history.</p>
      <ul className="space-y-2">
        {session.items.map((i) => (
          <li key={i.dance.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3" data-testid={`us-outcome-${i.dance.slug}`}>
            <span className="flex-1 font-semibold text-[#2D1E18]">{i.dance.name}</span>
            {ACTS.map((a) => <button key={a} onClick={() => toggle(i.dance.id, a)} data-testid={`us-act-${i.dance.slug}-${a}`} className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase ${outcome[i.dance.id]?.has(a) ? "bg-[#C85A32] text-white" : "border border-border text-[#6E5B51]"}`}>{a}</button>)}
          </li>
        ))}
      </ul>
      <button className={`${btn} mt-3`} onClick={submit} data-testid="us-complete-submit"><CheckCircle2 size={15} /> {session.state === "completed" ? "Update completed session" : "Complete session"}</button>
    </div>
  );
}

export default function UnifiedSessions() {
  const qc = useQueryClient();
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("build");

  const { data: flags } = useQuery({ queryKey: ["us-flags"], queryFn: async () => (await api.get("/admin/us/flags")).data });
  const { data: classes } = useQuery({ queryKey: ["admin-classes-all"], queryFn: async () => (await api.get("/classes")).data });
  const { data: dances } = useQuery({ queryKey: ["admin-dances-all"], queryFn: async () => (await api.get("/dances?limit=1000")).data });
  const { data: sessions, refetch: refetchList } = useQuery({ queryKey: ["us-sessions", classId], enabled: !!classId, queryFn: async () => (await api.get(`/admin/us/sessions?class_id=${classId}`)).data });
  const { data: open, refetch: refetchOpen } = useQuery({ queryKey: ["us-session", openId], enabled: !!openId, queryFn: async () => (await api.get(`/admin/us/sessions/${openId}`)).data });
  const { data: audit } = useQuery({ queryKey: ["us-audit", openId], enabled: !!openId && tab === "audit", queryFn: async () => (await api.get(`/admin/us/sessions/${openId}/audit`)).data });
  const { data: cmp, refetch: refetchCmp } = useQuery({ queryKey: ["us-compare", classId], enabled: false, queryFn: async () => (await api.get(`/admin/us/compare?class_id=${classId}`)).data });
  const { data: bf, refetch: refetchBf } = useQuery({ queryKey: ["us-backfill"], enabled: false, queryFn: async () => (await api.post("/admin/us/backfill/dry-run")).data });

  const refresh = () => { refetchOpen(); refetchList(); qc.invalidateQueries({ queryKey: ["us-sessions", classId] }); };
  const createOrOpen = async () => {
    try { const { data } = await api.post("/admin/us/sessions", { class_id: classId, date }); setOpenId(data.id); setTab("build"); refetchList(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Class Sessions (Unified)" subtitle="Preview workflow — flags are OFF, so member pages still use the existing system. Writes go to an isolated store only.">
      <div className="mb-4 flex items-center gap-2 rounded-2xl border border-[#F3C9A8] bg-[#FEF6EC] px-4 py-3 text-sm text-[#9A5B1E]" data-testid="us-flag-banner">
        <Database size={18} /> Isolated preview. ENABLED: <b>{flags?.unified_sessions_enabled ? "on" : "off"}</b> · WRITE: <b>{flags?.unified_sessions_write ? "on" : "off"}</b>. Legacy Assignments & Sessions untouched; no double-writes.
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div><label className={lbl}>Class</label>
          <select className={inp} value={classId} onChange={(e) => { setClassId(e.target.value); setOpenId(null); }} data-testid="us-class-select">
            <option value="">Select a class…</option>
            {(classes || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div><label className={lbl}>Date</label><input type="date" className={inp} value={date} onChange={(e) => setDate(e.target.value)} data-testid="us-date" /></div>
        <button className={btn} onClick={createOrOpen} disabled={!classId} data-testid="us-create"><Plus size={15} /> New / open draft</button>
      </div>

      {classId && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button className={btnGhost} onClick={() => refetchCmp()} data-testid="us-run-compare"><GitCompare size={15} /> Compare old vs new</button>
          <button className={btnGhost} onClick={() => refetchBf()} data-testid="us-run-backfill"><FileSearch size={15} /> Backfill dry-run</button>
        </div>
      )}

      {cmp && (
        <div className="mt-4 rounded-2xl border border-border bg-card p-4 text-sm" data-testid="us-compare-result">
          <h3 className="font-display font-extrabold text-[#2D1E18]">Comparison (read-only)</h3>
          <p className="mt-1">New Dances (taught): old <b>{cmp.new_dances_taught.old}</b> · new <b>{cmp.new_dances_taught.new}</b></p>
          <p className="mt-1 text-xs text-[#6E5B51]">Playlist dates — old: {cmp.playlist_dates.old.length}, new: {cmp.playlist_dates.new.length}. {cmp.note}</p>
        </div>
      )}
      {bf && (
        <div className="mt-4 rounded-2xl border border-border bg-card p-4 text-sm" data-testid="us-backfill-result">
          <h3 className="font-display font-extrabold text-[#2D1E18]">Backfill dry-run</h3>
          <p className="mt-1">Reliable <b>{bf.records.reliable}</b> · Ambiguous <b>{bf.records.ambiguous}</b> · Duplicate <b>{bf.records.duplicate}</b> · Unmappable <b>{bf.records.unmappable}</b> · Already mapped <b>{bf.records.already_has_unified_session}</b></p>
          <p className="mt-1 text-xs text-[#6E5B51]">Would create {bf.totals.would_create_sessions} sessions from {bf.totals.assignments} assignments. {bf.note}</p>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <div>
          <h3 className="mb-2 font-display font-extrabold text-[#2D1E18]">Sessions</h3>
          {!classId ? <p className="text-sm text-[#6E5B51]">Select a class.</p> : (sessions || []).length === 0 ? <p className="text-sm text-[#6E5B51]">No sessions yet.</p> : (
            <ul className="space-y-2">
              {sessions.map((s) => (
                <li key={s.id}>
                  <button onClick={() => { setOpenId(s.id); setTab("build"); }} data-testid={`us-session-${s.id}`} className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left ${openId === s.id ? "border-[#C85A32]" : "border-border"}`}>
                    <span className="font-semibold text-[#2D1E18]">{formatDate(s.date, { weekday: "short", year: true })}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATE_STYLE[s.state]}`}>{s.state}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          {!open ? <EmptyState title="No session open" subtitle="Create or open a draft to begin." /> : (
            <div data-testid="us-session-panel">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <h2 className="font-display text-xl font-extrabold text-[#2D1E18]">{formatDate(open.date, { weekday: "short", year: true })}</h2>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase ${STATE_STYLE[open.state]}`} data-testid="us-state">{open.state}</span>
              </div>
              <div className="mb-4 flex gap-2 border-b border-border">
                {["build", "complete", "audit"].map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`us-tab-${t}`} className={`px-3 py-2 text-sm font-semibold capitalize ${tab === t ? "border-b-2 border-[#C85A32] text-[#C85A32]" : "text-[#6E5B51]"}`}>{t === "build" ? "Playlist" : t === "complete" ? "Record activities" : "Audit"}</button>)}
              </div>
              {tab === "build" && <Builder key={open.id + open.state} session={open} dances={dances} onChange={refresh} />}
              {tab === "complete" && (open.items.length ? <CompletePanel key={open.id + open.state} session={open} onChange={refresh} /> : <EmptyState title="Add dances first" />)}
              {tab === "audit" && (
                <div data-testid="us-audit">
                  {(audit || []).length === 0 ? <p className="text-sm text-[#6E5B51]">No audit entries yet.</p> : (
                    <ul className="space-y-2 text-sm">
                      {audit.map((a) => <li key={a.id} className="rounded-xl border border-border bg-card p-3"><b className="capitalize">{a.action.replace("_", " ")}</b> by {a.actor_name} · {formatDateTime(a.at)}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
